import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { FormValidationHelpers } from "../../../helpers/FormValidationHelpers";
import { MultiSelectTagPicker } from "../../_base/MultiSelectTagPicker";
import { IPersonaProps } from "../assignee/AssigneeFluent9";
import { Avatar, Link, makeStyles, tokens } from "@fluentui/react-components";

const useStyles = makeStyles({
    // Nested flex column so the From/To/CC pickers (and the Add Cc link) are spaced by the same gap the
    // parent RfiFluent9 `content` column applies between its fields, which a single child wrapper would
    // otherwise collapse to one gap slot.
    container: {
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacingVerticalL,
    },
    addCcButton: {
        display: "block",
        marginLeft: "auto",
        // Pulls the link up out of the container's standard field-to-field gap so it reads as
        // belonging to the To field right above it, rather than as its own separate field.
        marginTop: `calc(-1 * ${tokens.spacingVerticalL} + ${tokens.spacingVerticalXS})`,
        fontSize: tokens.fontSizeBase200,
    },
    optionEmailSubtext: {
        color: tokens.colorNeutralForeground3,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    optionTextColumn: {
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
    },
    optionName: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    optionSingleText: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
});

export interface FromAndToFluent9SimpleProps extends LocalizeContextProps {
    // Which of the three pickers to render.
    hasFrom: boolean;
    hasTo: boolean;
    hasCc: boolean;

    // Options: the full project team (members and groups) as personas. Owned by the parent (via the
    // useProjectTeamMembers hook) so this component stays presentational and has no data source.
    teamMembers: IPersonaProps[];
    formValidationHelpers: FormValidationHelpers;

    // Controlled selections: the parent is the single source of truth. The pickers render directly
    // from these and report edits through the onChange callbacks; there is no internal copy to drift.
    // A field left undefined defaults to empty (e.g. a consumer that doesn't render CC).
    fromValue?: IPersonaProps[];
    toValue?: IPersonaProps[];
    ccValue?: IPersonaProps[];

    disabled?: boolean;
    isLoadingTeamMembers?: boolean;
    isFromRequired?: boolean;
    isToRequired?: boolean;
    isCcRequired?: boolean;

    onFromChange?: (people: IPersonaProps[]) => void;
    onToChange?: (people: IPersonaProps[]) => void;
    onCcChange?: (people: IPersonaProps[]) => void;

    // Reports the combined format/required/to-cc-duplicate validity this component already computes
    // for its own inline messages, so callers don't need their own copy of the same checks (e.g. via
    // formValidationHelpers.areAssigneesValid) just to gate a Submit button.
    onValidationChange?: (isValid: boolean) => void;
}

// Stable empty array so omitted fields keep a constant identity across renders (memo deps).
const EMPTY_PERSONAS: IPersonaProps[] = [];

// Cap rendered options so large project teams (1000+) don't stall the dropdown.
const MAX_VISIBLE_TEAM_MEMBERS = 25;

// ---- pure helpers (module scope so their identities are stable) ----

// Combined "Name email" string so the picker can filter by either; this is also the key used to map a
// selected string back to its persona.
const personaToString = (persona: IPersonaProps): string => {
    const name = persona.text?.trim() || "";
    const email = persona.data?.email?.trim() || "";
    if (name && email && name.toLowerCase() !== email.toLowerCase()) {
        return `${name} ${email}`;
    }
    return name || email || "";
};

const personaEmail = (persona: IPersonaProps): string => (persona.data?.email ?? persona.text ?? "").trim();

const isGroup = (persona: IPersonaProps): boolean => !!persona.data?.nrn && persona.data.nrn.includes("nrn:teamgroup");

// Stable identity for a persona across its different string representations: a known team member or
// group is identified by its NRN, an ad-hoc email persona by its lowercased email. This is what lets
// a seeded "alice@example.com" and the team option "Alice alice@example.com" collapse to one entry
// even though their display keys (personaToString) differ.
const personaIdentity = (persona: IPersonaProps): string =>
    persona.data?.nrn?.trim() || personaEmail(persona).toLowerCase();

// De-duplicate personas by their string key, preferring the first occurrence (team members win over
// seeded plain-email personas that resolve to the same value).
const mergeByString = (primary: IPersonaProps[], extra?: IPersonaProps[]): IPersonaProps[] => {
    const seen = new Set<string>();
    const result: IPersonaProps[] = [];
    for (const persona of [...primary, ...(extra ?? [])]) {
        const key = personaToString(persona);
        if (!key || seen.has(key)) {
            continue;
        }
        seen.add(key);
        result.push(persona);
    }
    return result;
};

// Resolve selected strings back to personas using the field's available list; anything typed that is
// not a known option becomes a plain { text } persona (a custom email). Collapses personas that
// resolve to the same identity (e.g. a seeded "alice@example.com" and the team option
// "Alice alice@example.com") in a single pass, preferring the entry that carries team-member/group
// metadata (an NRN), and keeping the most recently selected identity last so From's slice(-1) still
// picks the latest selection.
const toPersonas = (selected: string[], available: IPersonaProps[]): IPersonaProps[] => {
    const map = new Map(available.map((persona) => [personaToString(persona), persona]));
    const byIdentity = new Map<string, IPersonaProps>();
    for (const value of selected) {
        const persona = map.get(value) ?? { text: value };
        const identity = personaIdentity(persona);
        if (!identity) {
            // No usable identity (blank/typed-but-empty): keep as-is, keyed by its raw value.
            byIdentity.set(value, persona);
            continue;
        }
        // Prefer the representation carrying an NRN; otherwise take the newer one. Re-inserting moves
        // the kept identity to the end so iteration order stays most-recent-last.
        const existing = byIdentity.get(identity);
        const kept = existing?.data?.nrn ? existing : persona;
        byIdentity.delete(identity);
        byIdentity.set(identity, kept);
    }
    return Array.from(byIdentity.values());
};

// Single-value variant of toPersonas: resolve one string back to its persona without building a Map
// or allocating intermediate arrays. Used by the per-item tag/option renderers.
const toPersona = (value: string, available: IPersonaProps[]): IPersonaProps =>
    available.find((persona) => personaToString(persona) === value) ?? { text: value };

const FromAndToFluent9SimpleComponent = (props: FromAndToFluent9SimpleProps): JSX.Element => {
    const styles = useStyles();
    const { teamMembers } = props;
    const fromValue = props.fromValue ?? EMPTY_PERSONAS;
    const toValue = props.toValue ?? EMPTY_PERSONAS;
    const ccValue = props.ccValue ?? EMPTY_PERSONAS;

    // The CC picker is hidden behind an "Add Cc" link until the user opts in or a CC value is present.
    // This is ephemeral view state (not domain data), so it stays local.
    const [showCcField, setShowCcField] = useState<boolean>(false);
    const isCcVisible = showCcField || ccValue.length > 0;

    // Per-field available personas: From excludes team groups; To/CC include everyone. The current
    // controlled values are merged in so custom/typed emails resolve back to a full persona.
    const nonGroupMembers = useMemo(() => teamMembers.filter((member) => !isGroup(member)), [teamMembers]);
    const fromAvailable = useMemo(() => mergeByString(nonGroupMembers, fromValue), [nonGroupMembers, fromValue]);
    const toAvailable = useMemo(() => mergeByString(teamMembers, toValue), [teamMembers, toValue]);
    const ccAvailable = useMemo(() => mergeByString(teamMembers, ccValue), [teamMembers, ccValue]);

    const fromOptions = useMemo(() => fromAvailable.map(personaToString), [fromAvailable]);
    const toOptions = useMemo(() => toAvailable.map(personaToString), [toAvailable]);
    const ccOptions = useMemo(() => ccAvailable.map(personaToString), [ccAvailable]);

    // The picker works in string values; derive them from the controlled personas each render.
    const fromSelected = useMemo(() => fromValue.map(personaToString), [fromValue]);
    const toSelected = useMemo(() => toValue.map(personaToString), [toValue]);
    const ccSelected = useMemo(() => ccValue.map(personaToString), [ccValue]);

    // To/CC may not share the same email address.
    const toEmails = useMemo(() => new Set(toValue.map(personaEmail).filter(Boolean)), [toValue]);
    const ccEmails = useMemo(() => new Set(ccValue.map(personaEmail).filter(Boolean)), [ccValue]);
    const hasDuplicateToCc = useMemo(() => Array.from(toEmails).some((email) => ccEmails.has(email)), [
        toEmails,
        ccEmails,
    ]);

    const duplicateMessage = `${props.translate("SHARED.ERRORS.TO_AND_CC_VALIDATION_1")}${props.translate(
        "SHARED.ERRORS.TO_AND_CC_VALIDATION_2"
    )}${props.translate("SHARED.ERRORS.TO_AND_CC_VALIDATION_3")}${props.translate(
        "SHARED.ERRORS.TO_AND_CC_VALIDATION_4"
    )}${props.translate("SHARED.ERRORS.TO_AND_CC_VALIDATION_5")}`;

    const emailValidationMessage = props.translate("SHARED.ERRORS.EMAIL_VALIDATION") as string;

    const loadingPlaceholder = props.translate("PROJECT_TEAM.LOADING_TEAM_MEMBERS") as string;
    const singleMemberPlaceholder = props.isLoadingTeamMembers
        ? loadingPlaceholder
        : (props.translate("PROJECT_TEAM.SELECT_TEAM_MEMBER") as string);
    const multiMemberPlaceholder = props.isLoadingTeamMembers
        ? loadingPlaceholder
        : (props.translate("PROJECT_TEAM.SELECT_TEAM_MEMBERS") as string);

    // A field has a format error if any selection is neither a known team member/group nor a valid
    // email address.
    const hasFormatError = (personas: IPersonaProps[]): boolean =>
        personas.some((persona) => {
            if (persona.data?.nrn) {
                return false; // known team member or group
            }
            return !props.formValidationHelpers.isValidEmail(personaEmail(persona));
        });

    const fromValidation = hasFormatError(fromValue) ? emailValidationMessage : "";
    const toValidation = hasFormatError(toValue) ? emailValidationMessage : hasDuplicateToCc ? duplicateMessage : "";
    const ccValidation = hasFormatError(ccValue) ? emailValidationMessage : hasDuplicateToCc ? duplicateMessage : "";

    // Same inputs as the three messages above, combined with each field's required/empty state (which
    // the inline messages don't need to know about) into the single pass/fail a Submit button wants.
    const isFieldValid = (personas: IPersonaProps[], required?: boolean): boolean =>
        (!required || personas.length > 0) && !hasFormatError(personas);
    const isValid =
        (!props.hasFrom || isFieldValid(fromValue, props.isFromRequired)) &&
        (!props.hasTo || isFieldValid(toValue, props.isToRequired)) &&
        (!props.hasCc || isFieldValid(ccValue, props.isCcRequired)) &&
        !hasDuplicateToCc;

    useEffect(() => {
        props.onValidationChange?.(isValid);
    }, [isValid, props.onValidationChange]);

    const handleFromChange = (selected: string[]): void => {
        // From is single-value (itemLimit = 1). Enforce the cap here at the data layer so a newly
        // picked person replaces the previous one instead of exceeding the limit, mirroring
        // AssigneeFluent9's `slice(-maxAllowed)` handling. Stays stateless: derived from the incoming
        // selection, keeping the most recently selected entry.
        props.onFromChange?.(toPersonas(selected, fromAvailable).slice(-1));
    };
    const handleToChange = (selected: string[]): void => {
        props.onToChange?.(toPersonas(selected, toAvailable));
    };
    const handleCcChange = (selected: string[]): void => {
        props.onCcChange?.(toPersonas(selected, ccAvailable));
    };

    const handleShowCc = (): void => {
        setShowCcField(true);
    };

    return (
        <div className={styles.container}>
            {props.hasFrom ? (
                <MultiSelectTagPicker
                    testid="fromandto-from"
                    label={props.translate("SHARED.FROM_AND_TO.FROM_LABEL") as string}
                    options={fromOptions}
                    selectedOptions={fromSelected}
                    onSelectionChange={handleFromChange}
                    allowCustomInput={true}
                    required={!!props.isFromRequired}
                    disabled={!!props.disabled}
                    itemLimit={1}
                    validationMessage={fromValidation}
                    displayFullLineTag={true}
                    maxVisibleOptions={MAX_VISIBLE_TEAM_MEMBERS}
                    renderTag={(opt) => <PersonaTag opt={opt} available={fromAvailable} />}
                    renderOption={(opt) => <PersonaOption opt={opt} available={fromAvailable} />}
                    placeholder={singleMemberPlaceholder}
                    showDownArrow={false}
                />
            ) : null}
            {props.hasTo ? (
                <MultiSelectTagPicker
                    testid="fromandto-to"
                    label={props.translate("SHARED.FROM_AND_TO.TO_LABEL") as string}
                    options={toOptions}
                    selectedOptions={toSelected}
                    onSelectionChange={handleToChange}
                    allowCustomInput={true}
                    required={!!props.isToRequired}
                    disabled={!!props.disabled}
                    validationMessage={toValidation}
                    displayFullLineTag={true}
                    maxVisibleOptions={MAX_VISIBLE_TEAM_MEMBERS}
                    renderTag={(opt) => <PersonaTag opt={opt} available={toAvailable} />}
                    renderOption={(opt) => <PersonaOption opt={opt} available={toAvailable} />}
                    placeholder={multiMemberPlaceholder}
                    showDownArrow={false}
                />
            ) : null}
            {props.hasCc && isCcVisible ? (
                <MultiSelectTagPicker
                    testid="fromandto-cc"
                    label={props.translate("SHARED.FROM_AND_TO.CC_LABEL") as string}
                    options={ccOptions}
                    selectedOptions={ccSelected}
                    onSelectionChange={handleCcChange}
                    allowCustomInput={true}
                    required={!!props.isCcRequired}
                    disabled={!!props.disabled}
                    validationMessage={ccValidation}
                    displayFullLineTag={true}
                    maxVisibleOptions={MAX_VISIBLE_TEAM_MEMBERS}
                    renderTag={(opt) => <PersonaTag opt={opt} available={ccAvailable} />}
                    renderOption={(opt) => <PersonaOption opt={opt} available={ccAvailable} />}
                    placeholder={multiMemberPlaceholder}
                    showDownArrow={false}
                />
            ) : null}
            {props.hasCc && !isCcVisible ? (
                <Link
                    className={styles.addCcButton}
                    data-testid="fromandto-add-cc-button"
                    disabled={!!props.disabled}
                    onClick={handleShowCc}
                >
                    {props.translate("SHARED.FROM_AND_TO.ADD_CC") as string}
                </Link>
            ) : null}
        </div>
    );
};

// Renders a selected tag as a square avatar + display name/email, carried over from
// AssigneeFluent9.renderAssigneeTag. `opt` is the picker's string value; it is resolved back to a
// persona using the field's available list so known team members show their full name.
interface PersonaTagProps {
    opt: string;
    available: IPersonaProps[];
}

const PersonaTag = ({ opt, available }: PersonaTagProps): JSX.Element => {
    const persona = toPersona(opt, available);
    const displayText = persona.text || persona.data?.email || "";
    const initialsSource = displayText.trim();
    let initials = "?";
    if (initialsSource) {
        initials = initialsSource.includes("@")
            ? initialsSource
                  .split("@")[0]
                  .slice(0, 2)
                  .toUpperCase()
            : initialsSource
                  .split(/\s+/)
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((part) => part[0])
                  .join("")
                  .toUpperCase();
    }

    return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
            <Avatar color="colorful" name={displayText || undefined} initials={initials} size={20} shape="square" />
            <div>{displayText}</div>
        </div>
    );
};

// Renders a dropdown option as a circular avatar + name/email, carried over from
// AssigneeFluent9.renderAssigneeOption. When a known team member has both a name and a distinct email
// the email is shown as subtext; otherwise just the display text. `opt` is the picker's string value,
// resolved back to a persona using the field's available list.
interface PersonaOptionProps {
    opt: string;
    available: IPersonaProps[];
}

const PersonaOption = ({ opt, available }: PersonaOptionProps): JSX.Element => {
    const styles = useStyles();
    const persona = toPersona(opt, available);
    const name = persona.text?.trim() || "";
    const email = persona.data?.email?.trim() || "";
    const hasNameAndEmail = !!name && !!email && name.toLowerCase() !== email.toLowerCase();
    const displayText = name || email || "";
    const initialsSource = displayText.trim();
    let initials = "?";
    if (initialsSource) {
        initials = initialsSource.includes("@")
            ? initialsSource
                  .split("@")[0]
                  .slice(0, 2)
                  .toUpperCase()
            : initialsSource
                  .split(/\s+/)
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((part) => part[0])
                  .join("")
                  .toUpperCase();
    }

    if (hasNameAndEmail) {
        return (
            <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: "8px" }}>
                <Avatar color="colorful" name={displayText || undefined} initials={initials} size={20} shape="square" />
                <div className={styles.optionTextColumn}>
                    <span className={styles.optionName}>{name}</span>
                    <small className={styles.optionEmailSubtext}>{email}</small>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: "8px" }}>
            <Avatar color="colorful" name={displayText || undefined} initials={initials} size={20} shape="square" />
            <span className={styles.optionSingleText}>{displayText}</span>
        </div>
    );
};

export default withLocalize(FromAndToFluent9SimpleComponent);
